import React from 'react';
import { useNavigate } from 'react-router-dom';

const LAST_UPDATED = 'May 2025';

const Card = ({ children, style = {} }) => (
  <div style={{ background: '#ffffff', border: '1px solid #E5E9F0', borderRadius: '1rem', padding: '1.75rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', ...style }}>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: '1.75rem' }}>
    <h2 style={{ color: '#0D1F3C', fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.75rem' }}>{title}</h2>
    <div style={{ color: '#374151', lineHeight: 1.75, fontSize: '0.9375rem' }}>{children}</div>
  </div>
);

const P = ({ children }) => <p style={{ marginBottom: '0.875rem' }}>{children}</p>;

const BulletList = ({ items }) => (
  <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 1rem' }}>
    {items.map((item, i) => (
      <li key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <span style={{ color: '#1A6FD6', fontWeight: 700, flexShrink: 0, marginTop: '0.1rem' }}>—</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Highlight = ({ title, children, type = 'info' }) => {
  const styles = {
    info:    { bg: '#EFF6FF', border: '#BFDBFE', icon: 'ℹ', iconColor: '#1A6FD6' },
    success: { bg: '#F0FDF4', border: '#BBF7D0', icon: '✓', iconColor: '#16A34A' },
    warning: { bg: '#FFFBEB', border: '#FDE68A', icon: '!', iconColor: '#D97706' },
  };
  const s = styles[type];
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
        <span style={{ color: s.iconColor, fontWeight: 700, fontSize: '1rem', flexShrink: 0, marginTop: '0.1rem' }}>{s.icon}</span>
        <div>
          {title && <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.25rem' }}>{title}</p>}
          <p style={{ margin: 0, color: '#374151', fontSize: '0.9rem', lineHeight: 1.6 }}>{children}</p>
        </div>
      </div>
    </div>
  );
};

const CancellationPolicy = () => {
  const navigate = useNavigate();

  return (
    <div style={{ background: '#FFFBEB', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

      <div style={{ background: '#ffffff', borderBottom: '1px solid #E5E9F0', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontWeight: 600, fontSize: '0.875rem' }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 12L6 8l4-4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back to Successlane
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#1A6FD6' }}></div>
            <span style={{ fontWeight: 700, color: '#0D1F3C', fontSize: '0.9rem' }}>Successlane</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2.5rem 1.5rem' }}>

        <div style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#1A6FD6', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Legal</p>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#0D1F3C', marginBottom: '0.5rem', lineHeight: 1.2 }}>Cancellation Policy</h1>
          <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>Last updated: {LAST_UPDATED} &nbsp;·&nbsp; Operated by <strong>Success Lane</strong></p>
        </div>

        <Card>
          <Highlight type="info" title="Quick summary">
            You can cancel your order and get a full refund if you write to us within <strong>30 minutes of successful payment</strong>. Beyond 30 minutes, refunds are only available under our <button onClick={() => navigate('/refund')} style={{ background: 'none', border: 'none', padding: 0, color: '#1A6FD6', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}>100-Day Quality Guarantee</button>.
          </Highlight>

          <Section title="1. The 30-minute cancellation window">
            <P>If you change your mind about your order, you may cancel it within <strong>30 minutes of successful payment</strong> and receive a 100% refund. No questions asked.</P>
            <P>To cancel within the 30-minute window, send an email to <a href="mailto:contact@successlane.io" style={{ color: '#1A6FD6' }}>contact@successlane.io</a> from the same email address used at checkout, with:</P>
            <BulletList items={[
              'Subject line: "Cancellation request — [your order ID]"',
              'A one-line note confirming that you wish to cancel.',
            ]} />
            <P>The timestamp of your email is what counts — provided it lands in our inbox within 30 minutes of payment confirmation, your cancellation is automatically accepted.</P>
          </Section>

          <Section title="2. Cancellation after 30 minutes">
            <P>Once 30 minutes have passed since successful payment, your order moves into active production:</P>
            <BulletList items={[
              'A domain-matched recruiter is allocated to your profile,',
              'Our team begins processing your intake form,',
              'Writing and review work is scheduled and committed.',
            ]} />
            <P>Because real human effort is now committed to your order, cancellation with refund is no longer available on a change-of-mind basis.</P>
            <P>After the 30-minute window, the only path to a refund is through our <button onClick={() => navigate('/refund')} style={{ background: 'none', border: 'none', padding: 0, color: '#1A6FD6', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}>100-Day Quality Guarantee</button>, which covers cases where you have implemented the deliverable and the measurable outcomes have not been met.</P>
          </Section>

          <Section title="3. Exceptions where cancellation is still possible">
            <P>Even after the 30-minute window, a cancellation with full refund will be honoured in the following situations:</P>
            <BulletList items={[
              'We are unable to deliver your order due to our own operational failure (e.g., no suitable recruiter available for your domain, technical error preventing intake).',
              'You were charged twice for the same order, or a payment was processed in error.',
              'Any other situation explicitly covered under the Refund Policy.',
            ]} />
          </Section>

          <Section title="4. How refunds reach you">
            <P>Approved cancellation refunds are processed back to the original payment method via Razorpay. Timelines depend on your payment method:</P>
            <BulletList items={[
              'UPI / Wallets: 1–3 business days',
              'Credit / Debit cards: 5–7 business days',
              'Net Banking: 3–5 business days',
            ]} />
            <P>You will receive an email confirmation when the refund has been initiated.</P>
          </Section>

          <Section title="5. Questions or help">
            <P>If you are unsure whether your cancellation request falls inside the 30-minute window — or you need help raising one — write to <a href="mailto:contact@successlane.io" style={{ color: '#1A6FD6' }}>contact@successlane.io</a> or call <a href="tel:+917259444336" style={{ color: '#1A6FD6' }}>+91 72594 44336</a> during working hours (Mon–Sat, 9:00 AM – 6:00 PM IST). We will always treat your request fairly and at face value.</P>
          </Section>
        </Card>

        <div style={{ borderTop: '1px solid #E5E9F0', paddingTop: '2rem', marginTop: '2.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <p style={{ color: '#9CA3AF', fontSize: '0.8125rem' }}>© 2025 Success Lane · Successlane</p>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/refund')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontSize: '0.8125rem', fontWeight: 500 }}>Refund</button>
            <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontSize: '0.8125rem', fontWeight: 500 }}>Terms</button>
            <button onClick={() => navigate('/privacy')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontSize: '0.8125rem', fontWeight: 500 }}>Privacy</button>
            <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: '0.8125rem' }}>Back to Home</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationPolicy;
